import Auth0Lock from 'auth0-lock';

import { EventEmitter } from 'events';

import config from '../config';
import { isTokenExpired } from '../utils/jwtHelpers';

const ID_TOKEN = 'id_token';
const ACCESS_TOKEN = 'access_token';
const PROFILE = 'profile';

export default class AuthService extends EventEmitter {
  lock = new Auth0Lock(config.AUTH_CLIENT_ID, config.AUTH_CLIENT_DOMAIN, {
    auth: {
      params: { scope: 'openid email app_metadata' },
      redirectUrl: `${config.ROOT_URL}/login`,
      responseType: 'token',
    },
  });

  constructor() {
    super();

    this.lock.on('authenticated', this.handleAuthenticated);
    this.lock.on('authorization_error', this.handleAuthorizationError);
  }

  handleAuthenticated = (authResult) => {
    this.setAccessToken(authResult.accessToken);
    this.setIdToken(authResult.idToken);

    this.lock.getUserInfo(authResult.accessToken, this.handleGetProfile);
  };

  handleGetProfile = (error, profile) => {
    if (error) {
      this.emit('auth_error', error);
    } else {
      this.setProfile(profile);
      window.location = '/';
    }
  };

  handleAuthorizationError = (authError) => {
    this.emit('auth_error', authError);
  };

  login = () => this.lock.show();

  logout() {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(ID_TOKEN);
    localStorage.removeItem(PROFILE);
  }

  loggedIn = () => {
    // Checks if there is a saved token and it's still valid
    const accessToken = this.getAccessToken();
    return !!accessToken && !isTokenExpired(accessToken);
  };

  setAccessToken(accessToken) {
    localStorage.setItem(ACCESS_TOKEN, accessToken);
  }

  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN);
  }

  setIdToken(idToken) {
    localStorage.setItem(ID_TOKEN, idToken);
  }

  getIdToken() {
    return localStorage.getItem(ID_TOKEN);
  }

  getHeaders = () => {
    const idToken = this.getIdToken();
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  };

  headersMultipartFormData = () => {
    const idToken = this.getIdToken();
    if (idToken) {
      return {
        Authorization: `Bearer ${idToken}`,
        Accept: '*/*',
        'X-Requested-With': 'XMLHttpRequest',
      };
    }
    return {};
  };

  setProfile = (profile) => {
    localStorage.setItem(PROFILE, JSON.stringify(profile));
    this.emit('profile_updated', profile);
  };

  getProfile() {
    // Retrieves the profile data from local storage
    const profile = localStorage.getItem(PROFILE);
    return profile ? JSON.parse(localStorage.profile) : {};
  }

  subscribe = (event, handler) => {
    this.on(event, handler);

    return {
      stop: () => this.removeListener(event, handler),
    };
  };

  subscribeToProfile = (handler) => {
    handler(this.getProfile());
    this.on('profile_updated', handler);

    return {
      stop: () => this.removeListener('profile_updated', handler),
    };
  };
}

export const auth = new AuthService();
